export interface Product {
  id: string;
  name: string;
  genericName: string;
  brand: string;
  strength: string;
  form: string;
  packSize: string;
  gtin: string;
  artgId: string;
  schedule: 'S2' | 'S3' | 'S4' | 'S8' | 'Unscheduled';
  category: string;
  subCategory: string;
  price: number;
  cost: number;
  margin: number;
  coldChain: boolean;
  hazardous: boolean;
  image: string;
  description: string;
  activeIngredient: string;
  manufacturer: string;
  status: 'active' | 'discontinued' | 'pending';
}

export interface StockItem {
  id: string;
  productId: string;
  branchId: string;
  location: string;
  bin: string;
  batchLot: string;
  expiryDate: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  costPrice: number;
  receivedDate: string;
  supplierId: string;
  quarantined: boolean;
  temperatureControlled: boolean;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  channel: 'pos' | 'phone' | 'web' | 'clickcollect' | 'marketplace';
  status: 'draft' | 'submitted' | 'accepted' | 'picking' | 'packed' | 'released' | 'delivered';
  priority: 'low' | 'medium' | 'high';
  orderDate: string;
  requiredDate: string;
  totalAmount: number;
  items: OrderItem[];
  notes: string;
  pharmacistId?: string;
  technicianId?: string;
  branchId: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  substituted: boolean;
  originalProductId?: string;
  batchLot?: string;
  expiryDate?: string;
}

export interface S8Entry {
  id: string;
  entryDate: string;
  entryType: 'receive' | 'issue' | 'transfer' | 'destruction' | 'adjustment';
  productId: string;
  productName: string;
  batchLot: string;
  quantity: number;
  balance: number;
  reason: string;
  pharmacistId: string;
  pharmacistName: string;
  witnessId?: string;
  witnessName?: string;
  witnessed: boolean;
  orderId?: string;
  supplierId?: string;
  notes: string;
  vaultLocation: string;
}

// Mock Products
export const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Panadol Extra',
    genericName: 'Paracetamol + Caffeine',
    brand: 'Panadol',
    strength: '500mg + 65mg',
    form: 'Tablet',
    packSize: '20',
    gtin: '9300675024914',
    artgId: 'AUST R 12345',
    schedule: 'S2',
    category: 'Pain Relief',
    subCategory: 'Analgesics',
    price: 8.99,
    cost: 5.50,
    margin: 38.8,
    coldChain: false,
    hazardous: false,
    image: '/api/placeholder/200/200',
    description: 'Fast effective relief from pain and fever',
    activeIngredient: 'Paracetamol, Caffeine',
    manufacturer: 'GSK Consumer Healthcare',
    status: 'active'
  },
  {
    id: '2',
    name: 'Ventolin HFA',
    genericName: 'Salbutamol',
    brand: 'Ventolin',
    strength: '100mcg/dose',
    form: 'Inhaler',
    packSize: '200 doses',
    gtin: '9300675024915',
    artgId: 'AUST R 67890',
    schedule: 'S4',
    category: 'Respiratory',
    subCategory: 'Bronchodilators',
    price: 12.99,
    cost: 8.20,
    margin: 36.9,
    coldChain: false,
    hazardous: false,
    image: '/api/placeholder/200/200',
    description: 'Relieves symptoms of asthma and COPD',
    activeIngredient: 'Salbutamol sulfate',
    manufacturer: 'GSK',
    status: 'active'
  }
];

// Mock Analytics Data
export const mockAnalytics = {
  kpis: {
    totalOrders: 156,
    totalRevenue: 12450.50,
    averageOrderValue: 79.81,
    fillRate: 94.2,
    cycleTime: 18.5,
    s8Compliance: 100,
    coldChainExceptions: 2,
    expiryRisk: 8
  },
  ordersByStatus: [
    { status: 'Draft', count: 5 },
    { status: 'Submitted', count: 12 },
    { status: 'Accepted', count: 8 },
    { status: 'Picking', count: 15 },
    { status: 'Packed', count: 6 },
    { status: 'Released', count: 3 },
    { status: 'Delivered', count: 107 }
  ],
  revenueByCategory: [
    { category: 'Pain Relief', revenue: 3200, color: '#EF4444' },
    { category: 'Cardiovascular', revenue: 2800, color: '#F97316' },
    { category: 'Respiratory', revenue: 2100, color: '#EAB308' }
  ],
  stockLevels: [
    { product: 'Panadol Extra', current: 43, minimum: 20, maximum: 100, status: 'good' },
    { product: 'Ventolin HFA', current: 22, minimum: 15, maximum: 50, status: 'good' }
  ]
};

// Mock Orders
export const mockOrders: Order[] = [
  {
    id: '1',
    orderNumber: 'ORD-2024-001',
    customerId: 'cust-1',
    customerName: 'John Smith',
    customerPhone: '0412 345 678',
    customerEmail: 'john.smith@email.com',
    channel: 'web',
    status: 'submitted',
    priority: 'medium',
    orderDate: '2024-09-15T09:30:00Z',
    requiredDate: '2024-09-15T16:00:00Z',
    totalAmount: 33.98,
    branchId: 'branch-1',
    notes: 'Customer prefers generic alternatives where available',
    items: [
      {
        id: '1',
        productId: '1',
        productName: 'Panadol Extra 20s',
        quantity: 2,
        unitPrice: 8.99,
        totalPrice: 17.98,
        substituted: false
      }
    ]
  }
];

// Mock Stock
export const mockStock: StockItem[] = [
  {
    id: '1',
    productId: '1',
    branchId: 'branch-1',
    location: 'Main Floor',
    bin: 'A-01-15',
    batchLot: 'LOT001',
    expiryDate: '2025-06-15',
    quantity: 48,
    reservedQuantity: 5,
    availableQuantity: 43,
    costPrice: 5.50,
    receivedDate: '2024-01-15',
    supplierId: 'supplier-1',
    quarantined: false,
    temperatureControlled: false
  }
];

// Mock S8 Entries
export const mockS8Entries: S8Entry[] = [
  {
    id: '1',
    entryDate: '2024-09-15T08:30:00Z',
    entryType: 'receive',
    productId: '3',
    productName: 'OxyContin 10mg 20s',
    batchLot: 'OXY240101',
    quantity: 20,
    balance: 28,
    reason: 'Stock receipt from supplier',
    pharmacistId: 'pharm-1',
    pharmacistName: 'Dr. Sarah Johnson',
    witnessId: 'tech-1',
    witnessName: 'Emma Wilson',
    witnessed: true,
    supplierId: 'supplier-3',
    notes: 'Received in good condition, temperature maintained',
    vaultLocation: 'VAULT-A1'
  }
];